/* global debounce */

if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.form.querySelector('[name="id"]').disabled = false;
        this.cartDrawer = document.querySelector('cart-drawer');
        this.submitBtn = this.querySelector('[name="add"]');
        this.defaultSubmitBtnText = this.submitBtn?.dataset.addToCartText || '';
        this.storeSelectionChangeHandler = this.storeSelectionChangeHandler
          || this.updatePrimaryButtonAction.bind(this);
        this.variantChangeHandler = this.variantChangeHandler
          || this.setMaximumInputQuantity.bind(this);
        this.form.addEventListener('submit', this.handleSubmit.bind(this));
        document.addEventListener('on:store:changed', this.storeSelectionChangeHandler);

        if (this.dataset.showQuantitySelector) {
          this.quantitySelector = this.querySelector('quantity-input');

          if (this.quantitySelector) {
            this.quantityInput = this.quantitySelector.querySelector('.qty-input__input');
            this.quantitySelector.addEventListener('change', debounce(this.handleChange.bind(this)));
            this.lineItemChangeListener = this.lineItemChangeListener
              || this.handleLineItemChange.bind(this);
            document.addEventListener('on:line-item:change', (evt) => {
              this.lineItemChangeListener(evt);
            });
          }
        }

        this.setMaximumInputQuantity();
        this.handleQuantityButtonState();
        document.addEventListener('on:variant:change', this.variantChangeHandler);
      }
    }

    disconnectedCallback() {
      document.removeEventListener('on:line-item:change', this.lineItemChangeListener);
      document.removeEventListener('on:store:changed', this.storeSelectionChangeHandler);
      document.removeEventListener('on:variant:change', this.variantChangeHandler);
    }

    getCurrentVariantId() {
      return Number(this.form?.querySelector('[name="id"]')?.value || this.dataset.variantId || 0);
    }

    getStoreSelector() {
      const productInfo = this.closest('.product-info') || this.closest('quick-add-drawer');
      return productInfo?.querySelector('store-selector-pdp, store-selector') || null;
    }

    getCurrentSelectedStore() {
      const selectorStore = this.getStoreSelector()?.getSelectedStore?.();
      if (selectorStore?.id) return selectorStore;

      const cartInfo = theme?.fulfillmentModeUtil?.parseCartInfo?.(
        this.form?.querySelector('[name="properties[_cart_info]"]')?.value
      );
      if (cartInfo?.store?.id) return cartInfo.store;

      return null;
    }

    getCurrentFulfillmentMode() {
      const selectorMode = this.getStoreSelector()?.getFulfillmentMode?.();
      if (selectorMode === 'click_collect') return 'click_collect';

      const cartInfoMode = theme?.fulfillmentModeUtil?.parseCartInfo?.(
        this.form?.querySelector('[name="properties[_cart_info]"]')?.value
      )?.mode;
      if (cartInfoMode === 'click_collect') return 'click_collect';

      return 'delivery';
    }

    getInventoryData() {
      const productInfo = this.closest('.product-info') || this.closest('quick-add-drawer');
      const dataEl = productInfo?.querySelector('.js-inventory-data');

      if (dataEl) {
        try {
          const rows = JSON.parse(dataEl.textContent || '[]');
          if (Array.isArray(rows)) return rows;
        } catch {
          // no-op
        }
      }

      const fallbackVariantId = this.getCurrentVariantId();
      if (!fallbackVariantId) return [];

      return [{
        id: fallbackVariantId,
        inventory_quantity: Number(this.dataset.inventoryQuantity || 0),
        available: this.dataset.available === 'true',
        inventory_policy: this.dataset.inventoryPolicy || '',
        inventory_management: this.dataset.inventoryManagement || '',
        delivery_inventory_quantity: Number(
          this.dataset.deliveryInventoryQuantity || this.dataset.inventoryQuantity || 0
        ),
        delivery_available: this.dataset.deliveryAvailable
          ? this.dataset.deliveryAvailable === 'true'
          : this.dataset.available === 'true'
      }];
    }

    getInventoryRow(variantId = this.getCurrentVariantId()) {
      return this.getInventoryData().find((item) => Number(item.id) === Number(variantId)) || null;
    }

    getCurrentStoreInventoryData() {
      const productInfo = this.closest('.product-info') || this.closest('quick-add-drawer');
      const dataEl = productInfo?.querySelector('[data-store-selector-drawer] [data-store-inventory]')
        || productInfo?.querySelector('[data-store-inventory]');
      if (!dataEl) return null;

      try {
        const data = JSON.parse(dataEl.textContent || '{}');
        return data && typeof data === 'object' ? data : null;
      } catch {
        return null;
      }
    }

    getStoreQuantityLimit(variantId = this.getCurrentVariantId(), storeId = this.getCurrentSelectedStore()?.id) {
      if (!storeId) return null;

      const inventoryData = this.getCurrentStoreInventoryData();
      if (!inventoryData || Number(inventoryData.variantId) !== Number(variantId)) return null;

      const normalizedStoreId = String(storeId);
      const quantity = Number(
        inventoryData.inventories?.[normalizedStoreId]
        ?? inventoryData.inventories?.[Number(normalizedStoreId)]
      );

      if (!Number.isFinite(quantity)) return null;

      return Math.max(0, quantity);
    }

    getQuantityLimit(
      inventory,
      fulfillmentMode = this.getCurrentFulfillmentMode(),
      storeId = this.getCurrentSelectedStore()?.id
    ) {
      if (!inventory) return null;

      if (!inventory.inventory_management || inventory.inventory_policy === 'continue') return null;

      if (fulfillmentMode === 'click_collect') {
        const storeQuantity = this.getStoreQuantityLimit(inventory.id, storeId);
        if (storeQuantity !== null) return storeQuantity;
      }

      const quantity = fulfillmentMode === 'delivery'
        ? Number(inventory.delivery_inventory_quantity)
        : Number(inventory.inventory_quantity);

      if (!Number.isFinite(quantity)) return null;

      return Math.max(0, quantity);
    }

    getCartLineFulfillmentMode(item, fallbackMode = 'delivery') {
      const resolvedMode = theme?.fulfillmentModeUtil?.getLineFulfillmentMode?.(item, fallbackMode);
      if (resolvedMode === 'click_collect') return 'click_collect';

      if (resolvedMode === 'delivery') return 'delivery';

      return fallbackMode === 'click_collect' ? 'click_collect' : 'delivery';
    }

    /**
     * Sets the max attribute of the quantity input
     */
    setMaximumInputQuantity() {
      const productInfo = this.closest('.product-info') || this.closest('quick-add-drawer');
      if (productInfo) {
        const quantitySelector = this.querySelector('quantity-input');
        if (quantitySelector) {
          const quantityInput = quantitySelector.querySelector('.qty-input__input');
          const inventory = this.getInventoryRow();
          const quantityLimit = this.getQuantityLimit(
            inventory,
            this.getCurrentFulfillmentMode(),
            this.getCurrentSelectedStore()?.id
          );

          if (quantityLimit !== null) {
            quantityInput.max = quantityLimit;

            const currentQuantity = Number(quantityInput.value);
            if (quantityLimit > 0 && currentQuantity > 1 && currentQuantity > quantityLimit) {
              quantityInput.value = quantityInput.max;
            }
          } else {
            quantityInput.removeAttribute('max');
          }

          const minusButton = quantitySelector.querySelector('.btn--minus');
          const plusButton = quantitySelector.querySelector('.btn--plus');
          ProductForm.setQuantityButtonState(null, quantityInput, minusButton, plusButton);
        }
      }

      this.updatePrimaryButtonAction();
    }

    /**
     * Sets the max attribute of the quantity input
     */
    handleQuantityButtonState() {
      const productInfo = this.closest('.product-info') || this.closest('quick-add-drawer');
      if (productInfo) {
        const quantitySelector = this.querySelector('quantity-input');
        if (quantitySelector) {
          const quantityInput = quantitySelector.querySelector('.qty-input__input');
          const minusButton = quantitySelector.querySelector('.btn--minus');
          const plusButton = quantitySelector.querySelector('.btn--plus');

          plusButton.addEventListener('click', () => {
            ProductForm.setQuantityButtonState('up', quantityInput, minusButton, plusButton);
          });

          minusButton.addEventListener('click', () => {
            ProductForm.setQuantityButtonState('down', quantityInput, minusButton, plusButton);
          });

          ProductForm.setQuantityButtonState(null, quantityInput, minusButton, plusButton);
        }
      }
    }

    static setQuantityButtonState(direction, quantityInput, minusButton, plusButton) {
      const currentQuantity = Number(quantityInput.value);
      const max = Number(quantityInput.max);
      const min = Number(quantityInput.min);

      let newQuantity = currentQuantity;

      if (direction === 'up') {
        newQuantity = currentQuantity + 1;
      } else if (direction === 'down') {
        newQuantity = currentQuantity - 1;
      }

      // Enable or disable buttons based on the newQuantity relative to max and min
      minusButton.disabled = newQuantity <= min;
      plusButton.disabled = max > 0 && newQuantity >= max;
    }

    getPrimaryButtonTextElement() {
      return this.submitBtn?.querySelector('.product-card-btn-text') || null;
    }

    getPrimaryButtonSrElement() {
      return this.submitBtn?.querySelector('.visually-hidden') || null;
    }

    getCardStockStatusElement(variantId = this.getCurrentVariantId()) {
      const quickAddDrawer = this.closest('quick-add-drawer');
      if (quickAddDrawer) {
        return quickAddDrawer.querySelector(
          `.variant-card[data-variant-id="${variantId}"] .card__stock-status`
        );
      }

      return this.closest('product-card')?.querySelector('.card__stock-status') || null;
    }

    hasStoreStockOnCard(storeId, variantId = this.getCurrentVariantId()) {
      if (!storeId) return false;

      const stockStatus = this.getCardStockStatusElement(variantId);
      if (!stockStatus) return false;

      try {
        const inventories = JSON.parse(stockStatus.dataset.inventories || '{}');
        return Number(inventories[String(storeId)]) > 0;
      } catch {
        return false;
      }
    }

    updatePrimaryButtonLabel(text) {
      if (!this.submitBtn) return;

      const normalizedText = String(text || '').trim();
      const visibleLabel = this.getPrimaryButtonTextElement();
      const srLabel = this.getPrimaryButtonSrElement();

      if (visibleLabel) visibleLabel.textContent = normalizedText;

      if (srLabel) srLabel.textContent = normalizedText;
    }

    resetPrimaryButtonAction() {
      if (!this.submitBtn || this.getStoreSelector()) return;

      const inventory = this.getInventoryRow();
      const storedSelection = window.StoreSelector?.readSelection?.() || null;
      const currentStore = this.getCurrentSelectedStore() || storedSelection?.store || null;
      const clickCollectSelected = storedSelection?.mode === 'click_collect' && currentStore?.id;
      const canAddViaSelectedStore = clickCollectSelected
        ? this.hasStoreStockOnCard(currentStore.id)
        : false;
      const deliveryLimit = this.getQuantityLimit(inventory, 'delivery');
      const deliveryAvailable = deliveryLimit === null
        ? inventory?.delivery_available === true
        : deliveryLimit > 0;

      delete this.submitBtn.dataset.storeSelectorCta;
      delete this.submitBtn.dataset.storeSelectorUrl;
      this.submitBtn.disabled = !(deliveryAvailable || canAddViaSelectedStore);
      if (this.submitBtn.disabled) {
        this.submitBtn.setAttribute('aria-disabled', 'true');
      } else {
        this.submitBtn.removeAttribute('aria-disabled');
      }

      if (this.defaultSubmitBtnText) this.updatePrimaryButtonLabel(this.defaultSubmitBtnText);
    }

    updatePrimaryButtonAction() {
      if (!this.submitBtn || this.getStoreSelector()) return;

      const variantId = this.getCurrentVariantId();
      const inventory = this.getInventoryRow(variantId);
      const deliveryLimit = this.getQuantityLimit(inventory, 'delivery');
      const inStockCount = this.#getInStockStoreCount(variantId);
      const storedSelection = window.StoreSelector?.readSelection?.() || null;
      const currentStore = this.getCurrentSelectedStore() || storedSelection?.store || null;
      const clickCollectSelected = storedSelection?.mode === 'click_collect' && currentStore?.id;
      const selectedStoreHasStock = clickCollectSelected
        ? this.hasStoreStockOnCard(currentStore.id, variantId)
        : false;

      if (deliveryLimit === 0 && inStockCount > 0 && !selectedStoreHasStock) {
        this.#showStoreSelectorCta(variantId);
        return;
      }

      this.resetPrimaryButtonAction();
    }

    /**
     * Handles 'change' events on the line item (dispatched by the theme).
     * @param {object} evt - Event object.
     */
    handleLineItemChange(evt) {
      if (evt.detail.variantId.toString() === this.dataset.variantId) {
        debounce(this.updateQuantityState(evt.detail.newQuantity));
      }
    }

    /**
     * Handles 'change' events on the quantity-input element.
     * @param {object} evt - Event object.
     */
    handleChange(evt) {
      this.updateQuantity(evt.target.value);
    }

    normalizeCartStore(store) {
      const normalized = window.StoreSelector?.normalizeStore?.(store);
      if (normalized?.id) return normalized;

      if (!store?.id) return null;

      return {
        id: String(store.id),
        name: String(store.name || ''),
        address: String(store.address || ''),
        phone: String(store.phone || store.store_phone || store.storePhone || ''),
        salonPhone: String(store.salonPhone || store.salon_phone || store.salonPhoneNumber || '')
      };
    }

    normalizeCartStoreList(stores) {
      const normalizedStores = theme?.fulfillmentModeUtil?.normalizeStoreList?.(stores);
      if (Array.isArray(normalizedStores)) return normalizedStores;

      const list = Array.isArray(stores) ? stores : [];
      const seen = new Set();
      return list.reduce((result, store) => {
        const normalized = typeof store === 'string' || typeof store === 'number'
          ? this.normalizeCartStore({ id: store })
          : this.normalizeCartStore(store);
        const storeId = String(normalized?.id || '').trim();
        if (!storeId || seen.has(storeId)) return result;

        seen.add(storeId);
        result.push(normalized);
        return result;
      }, []);
    }

    addCartStore(stores, store) {
      const list = this.normalizeCartStoreList(stores);
      const normalized = this.normalizeCartStore(store);
      const storeId = String(normalized?.id || '').trim();
      if (!storeId || list.some((item) => String(item.id) === storeId)) return list;

      return [...list, normalized];
    }

    getCartInfoStores(cart) {
      const stores = theme?.fulfillmentModeUtil?.getCartInfoStores?.(cart);
      if (Array.isArray(stores)) return this.normalizeCartStoreList(stores);

      const cartInfo = theme?.fulfillmentModeUtil?.parseCartInfo?.(cart?.attributes?._cart_info);
      return this.normalizeCartStoreList(cartInfo?.stores || []);
    }

    getCartAttributePayload(mode, store, options = {}) {
      const normalizedMode = mode === 'mixed_mode'
        ? 'mixed_mode'
        : mode === 'click_collect'
          ? 'click_collect'
          : 'delivery';
      const selectedStore = normalizedMode === 'click_collect' && store?.id
        ? this.normalizeCartStore(store)
        : null;
      const stores = this.normalizeCartStoreList(options.stores);
      const cartInfo = theme?.fulfillmentModeUtil?.stringifyCartInfo
        ? theme.fulfillmentModeUtil.stringifyCartInfo(normalizedMode, selectedStore, { stores })
        : JSON.stringify({
            version: 1,
            mode: normalizedMode,
            store_id: selectedStore?.id || '',
            store_name: selectedStore?.name || '',
            store_address: selectedStore?.address || '',
            store_phone: selectedStore?.phone || '',
            salon_phone: selectedStore?.salonPhone || '',
            store_ids: this.normalizeCartStoreList([...stores, selectedStore].filter(Boolean))
              .map((item) => item.id)
              .join('|')
          });

      return { _cart_info: cartInfo };
    }

    cartAttributesMatch(cart, mode, store, options = {}) {
      const expected = this.getCartAttributePayload(mode, store, options);
      const attributes = cart?.attributes || {};

      /* Compare the fields that decide fulfillment, not the raw string. `_cart_info`
         also carries values that change on their own - the trace id and the
         selection timestamp - and comparing the whole string would report a
         mismatch on every add to cart, rewriting the attribute and every cart line
         for nothing. */
      return Object.entries(expected).every(([key, value]) => {
        if (key !== '_cart_info') return String(attributes[key] || '') === String(value || '');

        const parse = (raw) => {
          try {
            const parsed = JSON.parse(raw || '{}');
            return [parsed?.mode || '', parsed?.store_id || '', parsed?.store_ids || ''].join('|');
          } catch {
            return '';
          }
        };

        return parse(attributes[key]) === parse(value);
      });
    }

    async syncCartAttributes(mode, store, options = {}) {
      if (!theme?.routes?.cartUpdate) return { ok: false, cart: null };

      const body = {
        attributes: this.getCartAttributePayload(mode, store, options)
      };
      const sections = Array.isArray(options.sections)
        ? [...new Set(options.sections.filter(Boolean))]
        : [];
      if (sections.length) {
        body.sections = sections;
        body.sections_url = window.location.pathname;
      }

      try {
        const response = await fetch(theme.routes.cartUpdate, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(body)
        });

        let cart = null;
        try {
          cart = await response.json();
        } catch {
          // no-op
        }

        return { ok: response.ok, cart };
      } catch (error) {
        console.error(error); // eslint-disable-line
        return { ok: false, cart: null };
      }
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      const storeSelectorUrl = this.submitBtn?.dataset.storeSelectorUrl || '';
      if (this.submitBtn?.dataset.storeSelectorCta === 'true' && storeSelectorUrl) {
        window.location.href = storeSelectorUrl;
        return;
      }

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      const formData = new FormData(this.form);
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      // Get all quick order lists on the page
      const quickOrderSections = [];
      document.querySelectorAll('quick-order-list').forEach((quickOrderList) => {
        const closestSection = quickOrderList.closest('.shopify-section');
        if (closestSection) {
          const sectionId = closestSection.id.replace('shopify-section-', '');
          // sections.push({
          //   id: `quick-order-list_${sectionId}`,
          //   section: sectionId,
          //   selector: `#quick-order-list_${sectionId}`
          // });
          sections += `,${sectionId}`;
        }
      });

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      try {
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);

        const oldCartData = await oldCartResponse.json();
        const fulfillmentUtil = theme.fulfillmentModeUtil;
        const storeSelector = this.getStoreSelector();
        const mixedFulfillmentError = theme.strings.storeSelector?.addToCartMixedFulfillmentError
          || theme.strings.cartError;
        const missingStoreError = theme.strings.storeSelector?.addToCartSelectStoreError
          || mixedFulfillmentError;
        if (!fulfillmentUtil?.resolveAddContext) {
          this.setErrorMsgState(theme.strings.cartError);
          return;
        }

        const selectorMode = storeSelector?.getFulfillmentMode?.() || null;
        const storedSelection = !storeSelector && window.StoreSelector?.readSelection
          ? window.StoreSelector.readSelection()
          : null;
        const cartResolvedMode = fulfillmentUtil.getResolvedCartMode(oldCartData);
        const cartResolvedStore = fulfillmentUtil.getStoreFromCart(oldCartData, cartResolvedMode);
        const requestedMode = selectorMode
          || fulfillmentUtil.getModeFromFormData(
            formData,
            storedSelection?.mode || cartResolvedMode
          )
          || cartResolvedMode;
        const requestedStore = storeSelector?.getSelectedStore?.()
          || fulfillmentUtil.getStoreFromFormData(formData)
          || (storedSelection?.store || null)
          || cartResolvedStore;
        const requestedQuantityForLine = Math.max(1, Number(formData.get('quantity') || 1));
        const addContext = fulfillmentUtil.resolveAddContext({
          cart: oldCartData,
          requestedMode,
          requestedStore
        });
        const shouldForceConciliation = Boolean(addContext.conflict?.hasConflict);
        const variantId = this.getCurrentVariantId();
        const inventory = this.getInventoryRow(variantId);
        const quantityLimit = this.getQuantityLimit(inventory, addContext.mode, addContext.store?.id);
        if (!addContext.ok) {
          this.setErrorMsgState(
            addContext.reason === 'missing_store'
              ? missingStoreError
              : mixedFulfillmentError
          );
          return;
        }

        if (addContext.mode === 'delivery' && quantityLimit !== null) {
          const fallbackMode = fulfillmentUtil.getCartFallbackMode(oldCartData);
          const existingDeliveryQuantity = oldCartData.items.reduce((total, item) => {
            if (item.variant_id !== variantId) return total;

            if (this.getCartLineFulfillmentMode(item, fallbackMode) !== 'delivery') return total;

            return total + (Number(item.quantity) || 0);
          }, 0);

          if (existingDeliveryQuantity + requestedQuantityForLine > quantityLimit) {
            if (quantityLimit === 0) {
              const inStockCount = this.#getInStockStoreCount(variantId);
              if (inStockCount > 0) {
                this.#showStoreSelectorCta(variantId);
                return;
              }
            }

            this.setErrorMsgState(
              theme.strings.cartQtyError.replace('[quantity]', quantityLimit)
            );
            return;
          }
        }

        if (addContext.mode === 'click_collect' && quantityLimit !== null && addContext.store?.id) {
          const existingStoreQuantity = oldCartData.items.reduce((total, item) => {
            if (item.variant_id !== variantId) return total;

            if (this.getCartLineFulfillmentMode(item) !== 'click_collect') return total;

            const lineStoreId = String(fulfillmentUtil.getStoreFromLineItem?.(item)?.id || '');
            const lineStoreName = String(fulfillmentUtil.getStoreNameFromLineItem?.(item) || '');
            if (lineStoreId && lineStoreId !== String(addContext.store.id)) return total;

            if (!lineStoreId && lineStoreName && lineStoreName !== String(addContext.store.name || '')) return total;

            return total + (Number(item.quantity) || 0);
          }, 0);

          if (existingStoreQuantity + requestedQuantityForLine > quantityLimit) {
            this.setErrorMsgState(
              theme.strings.cartQtyError.replace('[quantity]', quantityLimit)
            );
            return;
          }
        }

        const selectedStore = addContext.mode === 'click_collect' && addContext.store?.id
          ? addContext.store
          : null;
        if (fulfillmentUtil.clearFormDataFulfillment) {
          fulfillmentUtil.clearFormDataFulfillment(formData);
        } else {
          formData.delete('properties[_cart_info]');
          if (!String(formData.get('properties[_item_fulfillment_type]') || '').trim()) {
            formData.delete('properties[_item_fulfillment_type]');
          }
        }

        const representedStores = selectedStore
          ? this.addCartStore(this.getCartInfoStores(oldCartData), selectedStore)
          : this.getCartInfoStores(oldCartData);
        const syncMode = shouldForceConciliation ? 'mixed_mode' : addContext.mode;
        const syncStore = syncMode === 'click_collect' ? selectedStore : null;
        const syncStores = syncMode === 'mixed_mode'
          ? representedStores
          : syncMode === 'click_collect'
            ? this.addCartStore([], selectedStore)
            : [];
        const shouldSyncCartAttributes = !this.cartAttributesMatch(oldCartData, syncMode, syncStore, { stores: syncStores });

        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', '));
        }

        if (data.status) this.setErrorMsgState(error);

        if (!response.ok) throw new Error(response.status);

        /* Keep the store the buyer is browsing with, even when this line goes out
           for delivery: `writeSelection` with a null store removes it from
           storage, so the next add from a list would silently fall back to
           delivery instead of the store the buyer picked. */
        const persistedStore = selectedStore || window.StoreSelector?.readSelection?.()?.store || null;
        window.StoreSelector?.writeSelection?.(addContext.mode, persistedStore);

        // Notify PLP card stock badges of the current fulfillment selection.
        document.dispatchEvent(new CustomEvent('on:store:changed', {
          bubbles: true,
          detail: {
            store: selectedStore,
            fulfillmentMode: addContext.mode,
            _source: 'product-form-atc'
          }
        }));

        let renderData = data;
        let newCartData = ProductForm.isCartResponse(data) ? data : null;
        if (shouldSyncCartAttributes) {
          const cartAttributeSync = await this.syncCartAttributes(syncMode, syncStore, {
            stores: syncStores,
            sections: theme.settings.afterAtc === 'page' ? [] : sections.split(',')
          });

          if (cartAttributeSync.ok && cartAttributeSync.cart) {
            if (cartAttributeSync.cart.items?.length) {
              window.StoreSelector?.noteCartBinding?.(syncMode === 'click_collect' ? syncStore : null);
            }

            newCartData = cartAttributeSync.cart;
            if (cartAttributeSync.cart.sections) renderData = cartAttributeSync.cart;
          }
        }

        if (shouldForceConciliation && theme.settings.afterAtc !== 'page') {
          try {
            window.sessionStorage.setItem('cart-click-collect-conciliation-pending', '1');
          } catch {
            // no-op
          }
        }

        if (theme.settings.afterAtc === 'page') {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          // Update cart icon count.
          ProductForm.updateCartIcon(renderData);

          // If item was added from Quick Add drawer, show "Added to cart" message.
          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          setTimeout(() => {
            // Update cart drawer contents.
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(
                renderData,
                !quickAddDrawer && ((theme.settings.afterAtc === 'drawer_desktop' && theme.mediaMatches.md)
                  || theme.settings.afterAtc === 'drawer')
              );

              // Update the quick order lists on the page
              if (quickOrderSections.length) {
                quickOrderSections.forEach((quickOrderSection) => {
                  const sectionEl = document.getElementById(quickOrderSection.id);
                  if (!sectionEl) return;

                  const el = sectionEl.querySelector(quickOrderSection.selector) || sectionEl;
                  el.innerHTML = ProductForm.getElementHTML(
                    renderData.sections[quickOrderSection.section],
                    quickOrderSection.selector
                  );
                });
              }
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) {
                if (cartItems.dataset.empty === 'true') {
                  window.location.reload();
                } else {
                  cartItems.refreshCartItems();
                }
              }
            }
          }, 700);
        }

        const addedItem = ProductForm.getAddedItem(data, variantId);
        const addedVariantId = Number(addedItem?.variant_id || data.variant_id || variantId);
        const requestedModeForLine = addContext.mode === 'click_collect' ? 'click_collect' : 'delivery';
        const requestedStoreIdForLine = requestedModeForLine === 'click_collect' ? String(addContext.store?.id || '') : '';
        const requestedStoreNameForLine = requestedModeForLine === 'click_collect' ? String(addContext.store?.name || '') : '';
        const getCartLevelLineMode = (cartContext) => {
          const cartMode = fulfillmentUtil.getResolvedCartMode?.(cartContext);
          return cartMode === 'click_collect' || cartMode === 'delivery' ? cartMode : null;
        };

        const matchesRequestedFulfillment = (item, cartContext) => {
          const cartLineMode = getCartLevelLineMode(cartContext);
          const lineMode = cartLineMode
            || fulfillmentUtil.getLineFulfillmentMode(item, fulfillmentUtil.getCartFallbackMode?.(cartContext) || 'delivery');
          if (lineMode !== requestedModeForLine) return false;

          if (requestedModeForLine !== 'click_collect') return true;

          const cartStore = cartLineMode === 'click_collect'
            ? fulfillmentUtil.getStoreFromCart?.(cartContext, cartLineMode)
            : null;
          const lineStoreId = String(
            cartStore?.id || fulfillmentUtil.getStoreFromLineItem?.(item)?.id || ''
          );
          if (lineStoreId && requestedStoreIdForLine) return lineStoreId === requestedStoreIdForLine;

          const lineStoreName = String(fulfillmentUtil.getStoreNameFromLineItem?.(item) || '');
          return lineStoreName === requestedStoreNameForLine;
        };

        const itemInOldCart = oldCartData.items.find((item) => (
          item.variant_id === addedVariantId && matchesRequestedFulfillment(item, oldCartData)
        )) || null;
        const itemInNewCart = newCartData?.items?.find((item) => (
          item.variant_id === addedVariantId && matchesRequestedFulfillment(item, newCartData)
        )) || null;

        const addedQuantity = Number(addedItem?.quantity ?? data.quantity);
        let newQuantity = Number.isFinite(addedQuantity) ? addedQuantity : requestedQuantityForLine;

        // Check if product was already in the cart
        if (itemInOldCart) {
          newQuantity = Number.isFinite(Number(itemInNewCart?.quantity))
            ? Number(itemInNewCart.quantity)
            : newQuantity;

          const expectedQuantity = Number(itemInOldCart.quantity) + requestedQuantityForLine;
          if (this.cartDrawer && newQuantity < expectedQuantity) {
            await this.cartDrawer.refreshCartDrawer(true);
          }

          const eventDetail = {
            variantId: addedVariantId,
            oldQuantity: itemInOldCart.quantity,
            newQuantity
          };
          if (newCartData) eventDetail.cart = newCartData;

          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: eventDetail
          }));
        } else {
          newQuantity = Number.isFinite(Number(itemInNewCart?.quantity))
            ? Number(itemInNewCart.quantity)
            : newQuantity;
          const eventDetail = {
            variantId: addedVariantId,
            quantity: newQuantity
          };
          if (newCartData) eventDetail.cart = newCartData;

          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: eventDetail
          }));

          // Update the quantity selectors if needed
          if (this.quantitySelector) {
            this.setAttribute('data-show-quantity-selector', 'true');
            this.quantityInput.value = newQuantity;
            this.quantitySelector.currentQty = this.quantityInput.dataset.initialValue;
          }
        }
      } catch (error) {
        console.error(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg.textContent
          }
        }));

        if (this.cartDrawer) {
          await this.cartDrawer.refreshCartDrawer();
          setTimeout(() => {
            if (this.cartDrawer) this.cartDrawer.refreshCartDrawer();
          }, 450);
        }
      } finally {
        // Re-enable 'Add to Cart' button.
        this.submitBtn.classList.add('is-success');
        this.submitBtn.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        }, 1400);
      }
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble && response?.sections?.['cart-icon-bubble']) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    static getAddedItem(response, variantId) {
      if (Array.isArray(response?.items)) {
        return response.items.find((item) => Number(item.variant_id) === Number(variantId))
          || response.items[0]
          || null;
      }

      return response || null;
    }

    static isCartResponse(response) {
      return Boolean(response && Array.isArray(response.items) && Number.isFinite(Number(response.item_count)));
    }

    updateQuantityState(newQuantity) {
      this.quantityInput.value = newQuantity;
      this.quantitySelector.currentQty = newQuantity;
      this.setAttribute('data-show-quantity-selector', newQuantity > 0 ? 'true' : 'false');
    }

    async updateQuantity(quantity) {
      const requestedQuantity = Math.max(0, Number(quantity) || 0);
      const currentQuantity = parseInt(this.quantityInput.dataset.initialValue, 10);
      this.quantitySelector.setAttribute('aria-disabled', 'true');
      const lineErrors = this.querySelector('.js-product-form-errors');
      const quantityLimit = this.getCurrentFulfillmentMode() === 'delivery'
        && this.quantityInput.max !== ''
        ? Number(this.quantityInput.max)
        : null;

      if (Number.isFinite(quantityLimit) && requestedQuantity > quantityLimit) {
        if (lineErrors) {
          lineErrors.textContent = theme.strings.cartQtyError.replace('[quantity]', quantityLimit);
          lineErrors.hidden = false;
        }

        this.updateQuantityState(currentQuantity);
        this.quantitySelector.removeAttribute('aria-disabled');
        return;
      }

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      };

      const body = {
        id: this.dataset.variantId,
        quantity: requestedQuantity
      };
      const cartDrawerSections = this.cartDrawer?.getSectionsToRender?.().map((section) => section.section) || [];
      if (cartDrawerSections.length) {
        body.sections = [...new Set(cartDrawerSections)];
        body.sections_url = window.location.pathname;
      }

      fetchRequestOpts.body = JSON.stringify(body);

      let didUpdate = false;
      let didRenderCart = false;

      try {
        const response = await fetch(theme.routes.cartChange, fetchRequestOpts);
        const data = await response.json();

        if (!response.ok) throw new Error(data.errors || response.status);

        lineErrors.innerHTML = '';
        lineErrors.hidden = true;
        didUpdate = true;
        if (this.cartDrawer && data.sections) {
          await this.cartDrawer.renderContents(data, false);
          didRenderCart = true;
        }
      } catch (error) {
        if (/^[0-9]+$/.test(error.message)) {
          lineErrors.textContent = theme.strings.cartError;
        } else {
          lineErrors.textContent = error.message;
        }

        lineErrors.hidden = false;
        console.error(error); // eslint-disable-line

        this.updateQuantityState(currentQuantity);
      } finally {
        if (didUpdate) this.updateQuantityState(requestedQuantity);

        this.quantitySelector.removeAttribute('aria-disabled');
        if (this.cartDrawer && didUpdate && !didRenderCart) this.cartDrawer.refreshCartDrawer();
      }
    }

    /**
     * Gets the innerHTML of an element.
     * @param {string} html - Section HTML.
     * @param {string} selector - CSS selector for the element to get the innerHTML of.
     * @returns {string}
     */
    static getElementHTML(html, selector) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;

      const el = tmpl.content.querySelector(selector);
      return el ? el.innerHTML : '';
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.style.background = '';
      this.errorMsg.style.padding = '';
      this.errorMsg.style.border = '';
      this.errorMsg.style.color = '';

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');

          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }

    /**
     * Gets the number of stores that have stock for a given variant
     * by reading the data-in-stock-count attribute from the selected variant card.
     * @param {number} variantId - The variant ID to check.
     * @returns {number} The number of stores with stock, or 0 if unavailable.
     */
    #getInStockStoreCount(variantId) {
      const quickAddDrawer = this.closest('quick-add-drawer');
      if (quickAddDrawer) {
        const variantCard = quickAddDrawer.querySelector(
          `.variant-card[data-variant-id="${variantId}"] .card__stock-status`
        );
        if (variantCard) return Math.max(0, Number(variantCard.dataset.inStockCount) || 0);
      }

      const cardStockStatus = this.closest('product-card')?.querySelector('.card__stock-status');
      if (!cardStockStatus) return 0;

      return Math.max(0, Number(cardStockStatus.dataset.inStockCount) || 0);
    }

    /**
     * Gets the PDP URL for the current product and variant with the store
     * selector auto-open flag applied.
     * @param {number} variantId - The selected variant ID.
     * @returns {string}
     */
    #getStoreSelectorUrl(variantId) {
      const quickAddDrawer = this.closest('quick-add-drawer');
      const productUrl = quickAddDrawer?.productUrl
        || this.closest('product-card')?.querySelector('.js-prod-link')?.getAttribute('href')
        || this.closest('.js-product')?.querySelector('.js-prod-link')?.getAttribute('href')
        || '';
      if (!productUrl) return '';

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', variantId);
      url.searchParams.set('open_store_selector', 'true');
      return `${url.pathname}${url.search}${url.hash}`;
    }

    /**
     * Shows a store-selector CTA for items that can be bought via Click &
     * Collect but not delivered.
     * @param {number} variantId - The selected variant ID.
     * @returns {boolean}
     */
    #showStoreSelectorCta(variantId) {
      if (!this.submitBtn) return false;

      const strings = theme.strings.storeSelector || {};
      const storedSelection = window.StoreSelector?.readSelection?.() || null;
      const currentStore = this.getCurrentSelectedStore() || storedSelection?.store || null;
      const linkText = currentStore?.id
        ? (strings.changeStore || strings.changeStoreAria || 'Change store')
        : (strings.quickAddSelectStoreLink || strings.selectStoreAria || 'Select store');
      const storeUrl = this.#getStoreSelectorUrl(variantId);
      if (!storeUrl) return false;

      this.submitBtn.dataset.storeSelectorCta = 'true';
      this.submitBtn.dataset.storeSelectorUrl = storeUrl;
      this.submitBtn.disabled = false;
      this.submitBtn.removeAttribute('aria-disabled');
      this.submitBtn.classList.remove('is-loading');
      this.updatePrimaryButtonLabel(linkText);
      return true;
    }
  }

  customElements.define('product-form', ProductForm);
}
